import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getMangaById, allManga } from '@/mocks/manga';

export default function MangaDetail() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const manga = id ? getMangaById(id) : undefined;

  if (!manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-18">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">漫画未找到</h1>
            <p className="text-foreground-600 mb-6">抱歉，您要查看的漫画不存在或已下架。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const related = allManga
    .filter((m) => m.category === manga.category && m.id !== manga.id)
    .slice(0, 4);

  const categoryRoute = manga.category === '韩漫' ? '韩漫推荐' : manga.category === '日漫' ? '日漫大全' : '国漫排行榜';

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${manga.title} - ${manga.author} | 土豪漫画网`,
    description: manga.description,
    author: { '@type': 'Person', name: manga.author },
    datePublished: '2024-01-01',
    dateModified: manga.updatedAt,
    url: `https://tuhaomanhua.com/manga/${manga.id}`,
    image: manga.coverImage,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://tuhaomanhua.com/' },
      { '@type': 'ListItem', position: 2, name: categoryRoute, item: `https://tuhaomanhua.com/category/${categoryRoute}` },
      { '@type': 'ListItem', position: 3, name: manga.title },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <main className="pt-16 md:pt-18">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: categoryRoute, to: `/category/${categoryRoute}` },
                { label: manga.title },
              ]}
            />

            {/* Article Header */}
            <article className="w-full px-4 md:px-6 pt-6 pb-10">
              <div className="flex flex-col md:flex-row gap-8 md:gap-10">
                {/* Cover Image */}
                <div className="flex-shrink-0 mx-auto md:mx-0">
                  <div className="w-48 md:w-64 aspect-[3/4] rounded-xl overflow-hidden">
                    <img
                      src={manga.coverImage}
                      alt={manga.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-3 py-1 bg-accent-100 text-accent-700 rounded-full font-medium">
                      {manga.category}
                    </span>
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${manga.status === '连载中' ? 'bg-green-100 text-green-700' : 'bg-foreground-100 text-foreground-600'}`}>
                      {manga.status}
                    </span>
                  </div>

                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                    {manga.title}
                  </h1>

                  <p className="text-sm text-foreground-500 mb-5">
                    作者：<strong className="text-foreground-700">{manga.author}</strong>
                  </p>

                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-star-fill text-accent-500"></i>
                      <span className="text-lg font-bold text-foreground-900">{manga.rating}</span>
                      <span className="text-xs text-foreground-500">综合评分</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                      <i className="ri-eye-line"></i>
                      <span>{(manga.views / 10000).toFixed(0)}万 次阅读</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                      <i className="ri-file-list-3-line"></i>
                      <span>共 {manga.chapters} 话</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-600">
                      <i className="ri-calendar-line"></i>
                      <span>更新于 {manga.updatedAt}</span>
                    </div>
                  </div>

                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
                    {manga.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {manga.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            {/* Long Description */}
            <section className="w-full px-4 md:px-6 pb-10">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-4">作品简介</h2>
              <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
                <p className="text-sm md:text-base whitespace-pre-line">{manga.longDescription}</p>
              </div>
            </section>

            {/* Related Manga */}
            {related.length > 0 && (
              <section className="w-full px-4 md:px-6 pb-16 md:pb-20">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关{manga.category}推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {related.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/manga/${rel.id}`}
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300"
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={rel.coverImage}
                          alt={rel.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="font-heading font-semibold text-foreground-900 text-sm truncate group-hover:text-primary-500 transition-colors">
                          {rel.title}
                        </h3>
                        <div className="flex items-center gap-1 mt-1.5">
                          <i className="ri-star-fill text-accent-500 text-xs"></i>
                          <span className="text-xs text-foreground-600">{rel.rating}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}